import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { PDFDocument } from "pdf-lib";
import { Backdrop, Box, CircularProgress, Modal } from "@mui/material";
import html2pdf from "html2pdf.js";
import { saveAs } from "file-saver";
import moment from "moment";

import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import PaginantionComponent from "../../../components/common/Pagination/PaginationComponent";
import { nextPageClick, previouPageClick } from "../../../utils/pagination";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import useFetchData from "../../../hooks/useFetchData";
import { API_ENDPOINT } from "../../../services/api/resources";
import FilterBar from "../../../components/common/FilterBar/FilterBar";
import DDateInput from "../../../components/Dashboard/DashboardInputs/DDateInput";
import { emptyCheck } from "../../../utils/validations";
import DButtonOutlined from "../../../components/Dashboard/DashboardButtons/dButtonOutlined";
import PoPdfView from "../../../components/common/HtmlToPdf/PoPdfView";

import buyerPoClasses from "../../BuyerTabs/PoTab/PoTab.module.css";

function VendorPOTab() {
  const pdfRef = useRef();

  const loginData = useSelector((state) => state.loginData.value);
  const auth_token = loginData?.auth_token;
  const vendor_token =
    loginData?.access_type === "vendor contact"
      ? loginData?.user?.token
      : loginData?.vendor_contact_token;

  const [sortingColumnName, setSortingColumnName] = useState(null);
  const [sortingOrder, setSortingOrder] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [useServerFilter, setUseServerFilter] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [paginationData, setPaginationData] = useState();
  const [startDateSearchQuery, setStartDateSearchQuery] = useState({
    value: "",
    isValid: true,
  });
  const [endDateSearchQuery, setEndDateSearchQuery] = useState({
    value: "",
    isValid: true,
  });

  const [poData, setPoData] = useState([]);
  const [tableProps, setTableProps] = useState({
    isLoading: false,
    errorMessage: "",
  });

  const [openPoPdfModal, setOpenPoPdfModal] = useState(false);
  const [generateViewPdfFlag, setGenerateViewPdfFlag] = useState(false);
  const [generateDownloadPdfFlag, setGenerateDownloadPdfFlag] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [viewPO, setViewPo] = useState({});

  const { PO_number } = viewPO;

  const idParams = useMemo(
    () => [
      {
        key: "vendor_token",
        value: vendor_token ? vendor_token : "",
      },
    ],
    [vendor_token],
  );

  const normalizeStatusFilterForApi = (status) => {
    const normalized = String(status || "").trim().toLowerCase();
    if (!normalized) return "";
    if (normalized === "approved") return "approved";
    if (normalized === "cancelled" || normalized === "canceled") return "cancelled";
    if (normalized === "waitingforapproval" || normalized === "waiting for approval") {
      return "waiting_for_approval";
    }
    return status;
  };

  const hasRealPoRfqCode = useMemo(
    () =>
      Array.isArray(poData) &&
      poData.some((row) => {
        const value = String(row?.rfq_code || "").trim();
        return value && value.toUpperCase() !== "NA";
      }),
    [poData],
  );

  const renderPoRfqCodeCell = useCallback((row) => {
    const rawValue = String(row?.rfq_code || "").trim();
    const value = rawValue || "-";
    const hasMeaningfulValue = value !== "-" && value.toUpperCase() !== "NA";

    return (
      <span
        title={hasMeaningfulValue ? value : ""}
        style={{
          display: "block",
          whiteSpace: hasMeaningfulValue ? "normal" : "nowrap",
          overflowWrap: hasMeaningfulValue ? "anywhere" : "normal",
          wordBreak: hasMeaningfulValue ? "break-word" : "normal",
          lineHeight: 1.4,
        }}
      >
        {value}
      </span>
    );
  }, []);

  const headings = useMemo(
    () => [
      {
        label: "RFQ ID",
        field: "rfq_code",
        col_width: hasRealPoRfqCode ? "16rem" : "8rem",
        minWidth: hasRealPoRfqCode ? "12rem" : "8rem",
        renderCell: (row) => renderPoRfqCodeCell(row),
      },
      { label: "PO ID", field: "PO_number" },
      {
        label: "PO Date",
        field: "purchase_order_date",
        render: (row) =>
          row?.purchase_order_date
            ? moment(row.purchase_order_date).format("DD/MM/YYYY")
            : "-",
      },
      { label: "Item Name", field: "name_list" },
      {
        label: "Grand Total",
        field: "item_price",
        renderCell: (row) => {
          const items = Array.isArray(row?.item_list) ? row.item_list : [];
          const computed = items.reduce((sum, item) => {
            const totalPrice = Number(item?.total_price ?? item?.totalPrice ?? 0);
            const taxPercentage = Number(item?.tax ?? item?.tax_percent ?? item?.taxPct ?? 0);
            const taxableAmount =
              totalPrice + (Number.isFinite(taxPercentage) && taxPercentage > 0
                ? (totalPrice * taxPercentage) / 100
                : 0);
            return sum + taxableAmount;
          }, 0);

          const backendGrandTotal = Number(row?.pricing?.grand_total);
          const subtotal = Number(row?.pricing?.subtotal);
          const fallback = Number(row?.item_price);
          const amount =
            Number.isFinite(backendGrandTotal) && backendGrandTotal > 0
              ? backendGrandTotal
              : computed > 0
                ? computed
                : Number.isFinite(subtotal) && subtotal > 0
                  ? subtotal
                  : fallback;
          const roundedAmount = Number.isFinite(amount) ? Math.round(amount) : 0;
          const currency = row?.currency_type || row?.pricing?.currency_type || "INR";
          return `${roundedAmount.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} - ${currency}`;
        },
      },
      { label: "Supplier Name", field: "vendor_name" },
      {
        label: "Approval Status",
        field: "final_status",
        render: (row) => row?.final_status ?? "-",
      },
    ],
    [hasRealPoRfqCode, renderPoRfqCodeCell],
  );

  const waitForPoPdfFonts = async () => {
    if (typeof document !== "undefined" && document?.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch {
        // ignore
      }
    }

    await new Promise((resolve) => requestAnimationFrame(() => resolve()));
  };

  const createPoPdfBlob = async (filename) => {
    const input = pdfRef.current;
    if (!input) return null;

    await waitForPoPdfFonts();

    const options = {
      margin: [10, 10, 10, 10],
      filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      },
      jsPDF: {
        unit: "pt",
        format: "a4",
        orientation: "portrait",
      },
      pagebreak: {
        mode: ["css", "legacy"],
        before: [".po-terms-page"],
        avoid: [
          ".po-top-header",
          ".po-hero-row",
          ".po-kpi-row",
          ".po-party-row",
          ".po-line-items-section",
          ".po-total-row",
          ".po-bottom-bar",
          ".po-terms-section",
          ".po-footer-row",
        ],
      },
    };

    return html2pdf().set(options).from(input).outputPdf("blob");
  };

  const generateViewPdf = async () => {
    try {
      const filename = `PO_#${PO_number || "NA"}.pdf`;
      const pdfBlob = await createPoPdfBlob(filename);
      if (!pdfBlob) return;
      setPdfData(URL.createObjectURL(pdfBlob));
    } finally {
      setGenerateViewPdfFlag(false);
    }
  };

  const downloadPOPdf = async (_token, row) => {
    setViewPo(row);
    setGenerateDownloadPdfFlag(true);
  };

  const generateDownloadPOPdf = async () => {
    try {
      const filename = `PO_#${PO_number || "NA"}.pdf`;
      const pdfBlob = await createPoPdfBlob(filename);
      if (!pdfBlob) return;
      const existingPdfBytes = await pdfBlob.arrayBuffer();
      const pdfDoc = await PDFDocument.load(existingPdfBytes);

      pdfDoc.setTitle("Compressed PDF");
      pdfDoc.setAuthor("Bidz.AI");

      const compressedPdfBytes = await pdfDoc.save({
        useObjectStreams: false,
        updateFieldAppearances: false,
      });

      const compressedPdfBlob = new Blob([compressedPdfBytes], {
        type: "application/pdf",
      });

      saveAs(compressedPdfBlob, filename);
    } finally {
      setGenerateDownloadPdfFlag(false);
    }
  };

  const openViewPo = async (_token, row) => {
    if (!row) return;
    setViewPo(row);
    setGenerateViewPdfFlag(true);
    setOpenPoPdfModal(true);
  };

  const GetAllPoData = (page, status) => {
    const statusFilterForApi = useServerFilter
      ? normalizeStatusFilterForApi(status)
      : status;

    useFetchData({
      pageNumber: page,
      endpoint: `${API_ENDPOINT.get_vendor_purchase_order_list}`,
      idParams,
      searchQuery,
      statusFilterQuery: statusFilterForApi,
      startDateQuery: startDateSearchQuery?.value,
      endDateQuery: endDateSearchQuery?.value,
      auth_token,
      ...(sortingColumnName && { sortingColumnName }),
      ...(sortingOrder && { sortingOrder }),
      setData: async (rows, responseData) => {
        setPoData(rows);
        return { paginationData: responseData };
      },
      setPaginationData,
      tableProps,
      setTableProps,
    });
  };

  useEffect(() => {
    if (!auth_token || !vendor_token) return;
    GetAllPoData(pageNumber, filterQuery);
  }, [
    auth_token,
    vendor_token,
    pageNumber,
    searchQuery,
    filterQuery,
    startDateSearchQuery?.value,
    endDateSearchQuery?.value,
    sortingColumnName,
    sortingOrder,
  ]);

  useEffect(() => {
    if (generateViewPdfFlag) {
      generateViewPdf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateViewPdfFlag, viewPO]);

  useEffect(() => {
    if (generateDownloadPdfFlag) {
      generateDownloadPOPdf();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateDownloadPdfFlag, viewPO]);

  const itemsPerPage = paginationData?.current_page_count;
  const totalItems = paginationData?.page_count;
  const currentPage = 1;

  return (
    <div className={buyerPoClasses.users_tab_wrapper}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={generateDownloadPdfFlag}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className={buyerPoClasses.users_topHeader}>
        <div className={`${buyerPoClasses.lhs} flex`}>
          <HeaderTitle titleText={"Customer PO"} customclass={buyerPoClasses.dashHead} />
        </div>
      </div>

      <div className={buyerPoClasses.poSection}>
        <div className={buyerPoClasses.poListRow}>
          <div
            className={buyerPoClasses.search_add_wrapper}
            style={{
              marginLeft: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              className={`${buyerPoClasses.flex} ${buyerPoClasses.flex_align_end}`}
              style={{ gap: "1rem" }}
            >
              <SearchBar
                value={searchQuery}
                onChange={(query) => {
                  setSearchQuery(query);
                  setPageNumber(1);
                }}
              />
              <div className={`${buyerPoClasses.flex}`} style={{ gap: "1rem" }}>
                <DDateInput
                  type={"date"}
                  label={"Start Date"}
                  disableFuture={true}
                  onChange={(e) => {
                    setPageNumber(1);
                    setStartDateSearchQuery({
                      isValid: e ? emptyCheck(e) : true,
                      value: e,
                    });
                  }}
                  value={startDateSearchQuery}
                  error={!startDateSearchQuery?.isValid}
                  errorMsg={"From date is required"}
                />
                <DDateInput
                  type={"date"}
                  label={"End Date"}
                  disableFuture={true}
                  onChange={(e) => {
                    setPageNumber(1);
                    setEndDateSearchQuery({
                      isValid: e ? emptyCheck(e) : true,
                      value: e,
                    });
                  }}
                  value={endDateSearchQuery}
                  error={!endDateSearchQuery?.isValid}
                  errorMsg={"To date is required"}
                />
              </div>
            </div>

            <div
              className={`${buyerPoClasses.flex} ${buyerPoClasses.flex_align_end}`}
              style={{ gap: "1rem" }}
            >
              <FilterBar
                filterValue={filterQuery}
                onFilterChange={(query) => {
                  setFilterQuery(query);
                  setPageNumber(1);
                  setUseServerFilter(Boolean(query));
                }}
              />
            </div>
          </div>

          <div className={`${buyerPoClasses.d_dataTable} `}>
            <DataTable
              headings={headings}
              tableData={(poData || []).slice().sort((a, b) => {
                const isA_NA = !a.rfq_code || a.rfq_code.trim() === "NA" || a.rfq_code.trim() === "";
                const isB_NA = !b.rfq_code || b.rfq_code.trim() === "NA" || b.rfq_code.trim() === "";
                if (isA_NA && !isB_NA) return 1;
                if (!isA_NA && isB_NA) return -1;
                return 0;
              })}
              tableSize={poData?.length === 0 ? "100%" : "100vw"}
              actions={true}
              errorMessage={tableProps.errorMessage}
              isLoading={tableProps.isLoading}
              viewBtn={true}
              editBtn={false}
              deleteBtn={false}
              sorting={true}
              downloadSendmailForPo={true}
              sendMail={true}
              Download={false}
              fileDownloadFirst={true}
              viewClick={openViewPo}
              downloadClick={downloadPOPdf}
              setSortingColumnName={setSortingColumnName}
              sortingColumnName={sortingColumnName}
              setSortingOrder={setSortingOrder}
              sortingOrder={sortingOrder}
              stickyFirstColumn={true}
            />

            <PaginantionComponent
              pageNumber={pageNumber}
              currentPage={currentPage || 0}
              itemsPerPage={itemsPerPage || 0}
              totalItems={totalItems || 0}
              previouPageClick={() => previouPageClick(pageNumber, setPageNumber)}
              localStorageKey={"vendor_po_page_number"}
              nextPageClick={() => nextPageClick(pageNumber, setPageNumber, paginationData)}
              setPageNumber={setPageNumber}
              disablePrevious={parseInt(pageNumber) === 1 || pageNumber === ""}
              disableNext={
                parseInt(pageNumber) === paginationData?.page_count ||
                !paginationData ||
                pageNumber === ""
              }
            />
          </div>
        </div>
      </div>

      <Modal
        open={openPoPdfModal}
        onClose={() => {
          setOpenPoPdfModal(false);
        }}
        aria-labelledby="pdf-preview-title"
        aria-describedby="pdf-preview-description"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            width: "100%",
          }}
        >
          <Box
            sx={{
              bgcolor: "background.paper",
              padding: "2px",
              justifyContent: "center",
              boxShadow: 24,
              width: "100%",
              maxHeight: "90%",
              height: "100%",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <h4
              style={{
                fontSize: "28px",
                textAlign: "center",
                marginBottom: "10px",
                textTransform: "uppercase",
                fontFamily: "gilroyMedium",
                fontWeight: 700,
              }}
            >
              PDF PO#{PO_number}
            </h4>

            <div className={buyerPoClasses.pdfWrapper}>
              {generateViewPdfFlag ? (
                <span className={`${buyerPoClasses.loader} ${buyerPoClasses.pdfLoader}`}>
                  <CircularProgress
                    thickness={6}
                    variant="indeterminate"
                    disableShrink
                    sx={{
                      animationDuration: "350ms",
                      color: "#55555580",
                    }}
                    size={25}
                  />
                  <p
                    style={{
                      display: "block",
                      fontSize: "12px",
                      marginLeft: "10px",
                      color: "#3c3636",
                    }}
                  >
                    Generating pdf...
                  </p>
                </span>
              ) : (
                pdfData && (
                  <iframe
                    src={pdfData}
                    width="100%"
                    height="100%"
                    title="pdf view"
                    style={{
                      height: "calc(100% - 65px)",
                      padding: "10px",
                      border: "1px solid #f2f2f2",
                      borderRadius: "6px",
                      width: "calc(100% - 20px)",
                      margin: "auto",
                    }}
                  ></iframe>
                )
              )}
              <DButtonOutlined
                label="Close"
                btnWidth={"12rem"}
                btnHeight={"4rem"}
                onClick={() => setOpenPoPdfModal(false)}
                buyerBtn={true}
                vendorBtnOutlined={false}
              />
            </div>
          </Box>
        </Box>
      </Modal>

      <div
        style={{
          position: "fixed",
          left: "-100000px",
          top: 0,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      >
        <PoPdfView pdfData={viewPO} ref={pdfRef} />
      </div>
    </div>
  );
}

export default VendorPOTab;
